import {
  ArrowPathIcon,
  ExclamationTriangleIcon,
  PencilIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { StripeSyncManager } from '../components/admin/StripeSyncManager';
import { API_URL } from '../constants/urls';

interface PlanConfiguration {
  id: string;
  plan_type: string;
  display_name: string;
  description: string | null;

  // User limits
  max_total_users: number | null;
  max_editors: number | null;

  // Feature limits
  max_propositions: number | null;
  max_segments: number | null;

  // Feature access
  genai_access: boolean;
  integrations_access: boolean;
  api_access: boolean;
  advanced_analytics: boolean;
  priority_support: boolean;

  // Pricing
  base_monthly_price: number;
  base_annual_price: number;
  per_editor_monthly_price: number | null;
  per_editor_annual_price: number | null;

  // Stripe integration
  stripe_product_id: string | null;
  stripe_monthly_price_id: string | null;
  stripe_annual_price_id: string | null;
  stripe_editor_monthly_price_id: string | null;
  stripe_editor_annual_price_id: string | null;

  // Cache
  cached_pricing: any | null;
  pricing_cache_expires: string | null;

  // Other
  trial_days: number;
  is_active: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
}

interface StripePrice {
  id: string;
  product_id: string;
  nickname: string | null;
  unit_amount: number;
  currency: string;
  recurring: {
    interval: string | null;
    interval_count: number | null;
  };
  active: boolean;
}

const SuperAdminBilling: React.FC = () => {
  const { user, token } = useAuth();
  const [plans, setPlans] = useState<PlanConfiguration[]>([]);
  const [stripePrices, setStripePrices] = useState<StripePrice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingPlan, setEditingPlan] = useState<PlanConfiguration | null>(
    null,
  );
  const [editForm, setEditForm] = useState<Partial<PlanConfiguration>>({});
  const [savingPlan, setSavingPlan] = useState<string | null>(null);
  const [syncingPlan, setSyncingPlan] = useState<string | null>(null);
  const [showStripePrices, setShowStripePrices] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/billing/plans`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPlans(response.data);
    } catch (err) {
      // console.error removed
      setError('Failed to fetch plan configurations');
    } finally {
      setLoading(false);
    }
  };

  const fetchStripePrices = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/admin/billing/stripe-prices`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setStripePrices(response.data.prices);
      setShowStripePrices(true);
    } catch (err) {
      // console.error removed
      alert('Failed to fetch Stripe prices');
    }
  };

  const startEdit = (plan: PlanConfiguration) => {
    setEditingPlan(plan);
    setEditForm({ ...plan });
    setShowEditModal(true);
  };

  const cancelEdit = () => {
    setShowEditModal(false);
    setEditingPlan(null);
    setEditForm({});
  };

  const saveChanges = async () => {
    if (!editingPlan || !editForm) return;

    setSavingPlan(editingPlan.id);
    try {
      const response = await axios.put(
        `${API_URL}/admin/billing/plans/${editingPlan.id}`,
        editForm,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      // Update local state
      setPlans(plans.map((p) => (p.id === editingPlan.id ? response.data : p)));
      setShowEditModal(false);
      setEditingPlan(null);
      setEditForm({});
    } catch (err) {
      // console.error removed
      alert('Failed to save plan configuration');
    } finally {
      setSavingPlan(null);
    }
  };

  const syncStripePrice = async (planId: string) => {
    setSyncingPlan(planId);
    try {
      const response = await axios.post(
        `${API_URL}/admin/billing/plans/${planId}/sync-stripe`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.success) {
        alert(response.data.message);
        fetchPlans(); // Refresh to show updated cached pricing
      }
    } catch (err) {
      // console.error removed
      alert('Failed to sync Stripe pricing');
    } finally {
      setSyncingPlan(null);
    }
  };

  const formatPrice = (price: number, currency = 'GBP') => {
    const symbol = currency === 'GBP' ? '£' : currency === 'EUR' ? '€' : '$';
    return `${symbol}${(price / 100).toFixed(2)}`;
  };

  const formatLimit = (limit: number | null) => {
    return limit === null ? 'Unlimited' : limit.toString();
  };

  if (!user || user.role !== 'super_admin') {
    return <div>Access denied: Super admin privileges required</div>;
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Error</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="border-b border-gray-200 pb-4">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Billing Configuration
            </h1>
            <p className="text-gray-600 mt-2">
              Manage plan configurations and Stripe integration
            </p>
          </div>
          <button
            onClick={fetchStripePrices}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            View Stripe Prices
          </button>
        </div>
      </div>

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {plans.map((plan) => (
          <div
            key={plan.id}
            className="bg-white rounded-lg shadow-lg overflow-hidden"
          >
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {plan.display_name}
                  </h3>
                  <p className="text-sm text-gray-500">{plan.plan_type}</p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => startEdit(plan)}
                    className="text-indigo-600 hover:text-indigo-900"
                    title="Edit"
                  >
                    <PencilIcon className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => syncStripePrice(plan.id)}
                    disabled={syncingPlan === plan.id}
                    className="text-blue-600 hover:text-blue-900 disabled:opacity-50"
                    title="Sync Stripe Pricing"
                  >
                    <ArrowPathIcon
                      className={`h-5 w-5 ${syncingPlan === plan.id ? 'animate-spin' : ''}`}
                    />
                  </button>
                </div>
              </div>

              {/* Pricing */}
              <div className="mb-4">
                <div className="text-2xl font-bold text-gray-900">
                  {formatPrice(plan.base_monthly_price)}/mo
                </div>
                <div className="text-sm text-gray-500">
                  or {formatPrice(plan.base_annual_price)}/year
                </div>
                {plan.per_editor_monthly_price && (
                  <div className="text-sm text-gray-600 mt-1">
                    +{formatPrice(plan.per_editor_monthly_price)} per editor/mo
                  </div>
                )}
              </div>

              {/* Limits */}
              <div className="space-y-1 mb-4">
                <div className="text-xs text-gray-600">
                  <span className="font-medium">Users:</span>{' '}
                  {formatLimit(plan.max_total_users)}
                </div>
                <div className="text-xs text-gray-600">
                  <span className="font-medium">Editors:</span>{' '}
                  {formatLimit(plan.max_editors)}
                </div>
                <div className="text-xs text-gray-600">
                  <span className="font-medium">Propositions:</span>{' '}
                  {formatLimit(plan.max_propositions)}
                </div>
                <div className="text-xs text-gray-600">
                  <span className="font-medium">Segments:</span>{' '}
                  {formatLimit(plan.max_segments)}
                </div>
              </div>

              {/* Features */}
              <div className="flex flex-wrap gap-1 mb-4">
                {plan.genai_access && (
                  <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded">
                    GenAI
                  </span>
                )}
                {plan.integrations_access && (
                  <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">
                    Integrations
                  </span>
                )}
                {plan.api_access && (
                  <span className="px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded">
                    API
                  </span>
                )}
                {plan.advanced_analytics && (
                  <span className="px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded">
                    Analytics
                  </span>
                )}
                {plan.priority_support && (
                  <span className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded">
                    Priority
                  </span>
                )}
              </div>

              {/* Status */}
              <div className="text-xs text-gray-500">
                {plan.is_active ? (
                  <span className="text-green-600">Active</span>
                ) : (
                  <span className="text-red-600">Inactive</span>
                )}
                {plan.cached_pricing && (
                  <span className="ml-2">
                    • Cached until{' '}
                    {plan.pricing_cache_expires
                      ? new Date(
                          plan.pricing_cache_expires,
                        ).toLocaleDateString()
                      : 'Unknown'}
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Edit Modal */}
      {showEditModal && editingPlan && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500/75 transition-opacity"
              onClick={cancelEdit}
            />

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">
                  Edit {editingPlan.display_name} Plan
                </h3>

                <div className="grid grid-cols-2 gap-4">
                  {/* Basic Info */}
                  <div className="col-span-2">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">
                      Basic Information
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Display Name
                        </label>
                        <input
                          type="text"
                          value={editForm.display_name || ''}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              display_name: e.target.value,
                            })
                          }
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Trial Days
                        </label>
                        <input
                          type="number"
                          value={editForm.trial_days || 0}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              trial_days: parseInt(e.target.value) || 0,
                            })
                          }
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        />
                      </div>
                    </div>
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700">
                        Description
                      </label>
                      <textarea
                        value={editForm.description || ''}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            description: e.target.value,
                          })
                        }
                        rows={2}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      />
                    </div>
                  </div>

                  {/* Limits */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">
                      User Limits
                    </h4>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Total Users
                        </label>
                        <input
                          type="number"
                          value={editForm.max_total_users || ''}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              max_total_users: e.target.value
                                ? parseInt(e.target.value)
                                : null,
                            })
                          }
                          placeholder="Leave empty for unlimited"
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Editors
                        </label>
                        <input
                          type="number"
                          value={editForm.max_editors || ''}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              max_editors: e.target.value
                                ? parseInt(e.target.value)
                                : null,
                            })
                          }
                          placeholder="Leave empty for unlimited"
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Feature Limits */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">
                      Feature Limits
                    </h4>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Propositions
                        </label>
                        <input
                          type="number"
                          value={editForm.max_propositions || ''}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              max_propositions: e.target.value
                                ? parseInt(e.target.value)
                                : null,
                            })
                          }
                          placeholder="Leave empty for unlimited"
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Segments
                        </label>
                        <input
                          type="number"
                          value={editForm.max_segments || ''}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              max_segments: e.target.value
                                ? parseInt(e.target.value)
                                : null,
                            })
                          }
                          placeholder="Leave empty for unlimited"
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Features */}
                  <div className="col-span-2">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">
                      Feature Access
                    </h4>
                    <div className="grid grid-cols-3 gap-4">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={editForm.genai_access || false}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              genai_access: e.target.checked,
                            })
                          }
                          className="rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">
                          GenAI Access
                        </span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={editForm.integrations_access || false}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              integrations_access: e.target.checked,
                            })
                          }
                          className="rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">
                          Integrations
                        </span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={editForm.api_access || false}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              api_access: e.target.checked,
                            })
                          }
                          className="rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">
                          API Access
                        </span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={editForm.advanced_analytics || false}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              advanced_analytics: e.target.checked,
                            })
                          }
                          className="rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">
                          Advanced Analytics
                        </span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={editForm.priority_support || false}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              priority_support: e.target.checked,
                            })
                          }
                          className="rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">
                          Priority Support
                        </span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={editForm.is_active || false}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              is_active: e.target.checked,
                            })
                          }
                          className="rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">
                          Plan Active
                        </span>
                      </label>
                    </div>
                  </div>

                  {/* Pricing */}
                  <div className="col-span-2">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">
                      Pricing (in pence/cents)
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Base Monthly Price
                        </label>
                        <input
                          type="number"
                          value={editForm.base_monthly_price || 0}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              base_monthly_price: parseInt(e.target.value) || 0,
                            })
                          }
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Base Annual Price
                        </label>
                        <input
                          type="number"
                          value={editForm.base_annual_price || 0}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              base_annual_price: parseInt(e.target.value) || 0,
                            })
                          }
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        />
                      </div>
                      {editingPlan.plan_type !== 'free' && (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">
                              Per Editor Monthly
                            </label>
                            <input
                              type="number"
                              value={editForm.per_editor_monthly_price || 0}
                              onChange={(e) =>
                                setEditForm({
                                  ...editForm,
                                  per_editor_monthly_price:
                                    parseInt(e.target.value) || 0,
                                })
                              }
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">
                              Per Editor Annual
                            </label>
                            <input
                              type="number"
                              value={editForm.per_editor_annual_price || 0}
                              onChange={(e) =>
                                setEditForm({
                                  ...editForm,
                                  per_editor_annual_price:
                                    parseInt(e.target.value) || 0,
                                })
                              }
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            />
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Stripe IDs */}
                  <div className="col-span-2">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">
                      Stripe Integration
                    </h4>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Product ID
                        </label>
                        <input
                          type="text"
                          value={editForm.stripe_product_id || ''}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              stripe_product_id: e.target.value,
                            })
                          }
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm font-mono text-xs"
                          placeholder="prod_..."
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Monthly Price ID
                          </label>
                          <input
                            type="text"
                            value={editForm.stripe_monthly_price_id || ''}
                            onChange={(e) =>
                              setEditForm({
                                ...editForm,
                                stripe_monthly_price_id: e.target.value,
                              })
                            }
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm font-mono text-xs"
                            placeholder="price_..."
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Annual Price ID
                          </label>
                          <input
                            type="text"
                            value={editForm.stripe_annual_price_id || ''}
                            onChange={(e) =>
                              setEditForm({
                                ...editForm,
                                stripe_annual_price_id: e.target.value,
                              })
                            }
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm font-mono text-xs"
                            placeholder="price_..."
                          />
                        </div>
                      </div>
                      {editingPlan.plan_type !== 'free' && (
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">
                              Editor Monthly Price ID
                            </label>
                            <input
                              type="text"
                              value={
                                editForm.stripe_editor_monthly_price_id || ''
                              }
                              onChange={(e) =>
                                setEditForm({
                                  ...editForm,
                                  stripe_editor_monthly_price_id:
                                    e.target.value,
                                })
                              }
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm font-mono text-xs"
                              placeholder="price_..."
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">
                              Editor Annual Price ID
                            </label>
                            <input
                              type="text"
                              value={
                                editForm.stripe_editor_annual_price_id || ''
                              }
                              onChange={(e) =>
                                setEditForm({
                                  ...editForm,
                                  stripe_editor_annual_price_id: e.target.value,
                                })
                              }
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm font-mono text-xs"
                              placeholder="price_..."
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  disabled={savingPlan === editingPlan.id}
                  onClick={saveChanges}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                >
                  {savingPlan === editingPlan.id ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  type="button"
                  onClick={cancelEdit}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stripe Prices Modal */}
      {showStripePrices && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div
              className="fixed inset-0 bg-gray-500/75"
              onClick={() => setShowStripePrices(false)}
            />
            <div className="relative bg-white rounded-lg max-w-4xl w-full p-6 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">Available Stripe Prices</h2>
                <button
                  onClick={() => setShowStripePrices(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-2">
                {stripePrices.map((price) => (
                  <div key={price.id} className="border rounded p-3 text-sm">
                    <div className="font-mono text-xs text-gray-600">
                      {price.id}
                    </div>
                    {price.nickname && (
                      <div className="font-medium">{price.nickname}</div>
                    )}
                    <div className="flex gap-4 text-xs text-gray-600 mt-1">
                      <span>
                        {formatPrice(
                          price.unit_amount,
                          price.currency.toUpperCase(),
                        )}
                      </span>
                      {price.recurring.interval && (
                        <span>
                          per {price.recurring.interval}
                          {price.recurring.interval_count &&
                            price.recurring.interval_count > 1 &&
                            ` (every ${price.recurring.interval_count} ${price.recurring.interval}s)`}
                        </span>
                      )}
                      <span
                        className={
                          price.active ? 'text-green-600' : 'text-red-600'
                        }
                      >
                        {price.active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stripe Sync Manager */}
      <div className="mt-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          Stripe Synchronization
        </h2>
        <StripeSyncManager />
      </div>
    </div>
  );
};

export default SuperAdminBilling;
